import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Modal } from 'antd';
import { multiPageTourSteps } from 'data/tourSteps';

import { selectIsLoading as selectApplicationsLoading } from 'features/applications/applicationsSelectors';
import { selectGuideCompletedPageTours } from 'features/auth/authSelectors';
import { selectIsLoading as selectJobsLoading } from 'features/jobs/jobsSelectors';

import { useStartTour } from 'hooks/tourHooks';
import useErrorCatch from 'hooks/useErrorCatch';
import { useAppDispatch, useAppSelector } from 'hooks/useTypedRedux';

import { dismissGuide } from 'utils/tour/dismissGuide';

import { HOME } from 'constants/path';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as LightningIcon } from 'assets/icons/lightning.svg';
import { ReactComponent as LogoIcon } from 'assets/icons/logo-blue.svg';
import { ReactComponent as PersonalCardIcon } from 'assets/icons/personal-card.svg';
import { ReactComponent as ShareIcon } from 'assets/icons/share.svg';

import styles from './GuideModal.module.scss';
import { GuideModalCard } from './components/GuideModalCard/GuideModalCard';

const WELCOME_SCREEN_MODAL_WIDTH_PX = '730px';

interface GuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GuideModal = ({ isOpen, onClose }: GuideModalProps): ReactElement => {
  const { t } = useTranslation();
  const startTour = useStartTour();
  const dispatch = useAppDispatch();
  const guideCompletedPageTours = useAppSelector(selectGuideCompletedPageTours);
  const catchError = useErrorCatch();
  const [isDisabled, setIsDisabled] = useState(true);
  const isJobsLoading = useAppSelector(selectJobsLoading);
  const isApplicationsLoading = useAppSelector(selectApplicationsLoading);
  const isJobsOrApplicationsDataLoading =
    isJobsLoading || isApplicationsLoading;

  const cardsConfig = [
    {
      icon: <PersonalCardIcon />,
      text: t('guideModal.feature1')
    },
    {
      icon: <ShareIcon />,
      text: t('guideModal.feature2')
    },
    {
      icon: <LightningIcon />,
      text: t('guideModal.feature3')
    }
  ];

  const handleStartGuide = () => {
    onClose();
    startTour(multiPageTourSteps, HOME, true);
  };

  const handleSkipPermanently = async () => {
    await dismissGuide({
      guideCompleted: guideCompletedPageTours,
      dispatch,
      catchError,
      errorMessage: t('error.message.finishTourFailed'),
      onClose
    });
  };

  useEffect(() => {
    if (isJobsOrApplicationsDataLoading) {
      setIsDisabled(true);
      return;
    }

    setIsDisabled(false);
  }, [isJobsOrApplicationsDataLoading]);

  return (
    <Modal
      className={styles.modal}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={WELCOME_SCREEN_MODAL_WIDTH_PX}
      closeIcon={<CloseIcon />}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.logoWrapper}>
            <LogoIcon width={140} height={64} />
          </div>
          <div className={styles.headerContentWrapper}>
            <div className={styles.title}>{t('guideModal.title')}</div>
            <div className={styles.subtitle}>{t('guideModal.subtitle')}</div>
          </div>
        </div>

        <div className={styles.actions}>
          {cardsConfig.map((el) => (
            <GuideModalCard text={el.text} icon={el.icon} key={el.text} />
          ))}
        </div>

        <div className={styles.buttons}>
          <Button
            className={styles.button}
            type="primary"
            onClick={handleStartGuide}
            disabled={isDisabled}
          >
            {t('guideModal.start')}
          </Button>
          <Button
            className={styles.button}
            type="link"
            onClick={onClose}
            disabled={isDisabled}
          >
            {t('guideModal.skip')}
          </Button>
          <Button
            className={styles.btnRed}
            type="link"
            onClick={handleSkipPermanently}
            disabled={isDisabled}
          >
            {t('guideModal.skipPermanently')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default GuideModal;
